/**
 * ContentEditor — TheBurntPeanut Simulator 9000
 * 
 * Tool for reviewing, renaming, and classifying all proposed new game content.
 * Design: dark industrial, Share Tech Mono, amber/red accent — matches game aesthetic.
 * 
 * Features:
 *  - Filter by category (weapons, percs, bullshiiii, shrines)
 *  - Rename each item inline
 *  - Toggle sprite type: Undecided / Sprite Only / Needs Animation
 *  - Add notes per item
 *  - Mark items as approved
 *  - Download full JSON or organized ZIP manifest
 *  - Progress bar showing approved count
 */

import { useState, useMemo, useCallback } from 'react';
import {
  NEW_CONTENT_ITEMS,
  CATEGORIES,
  SPRITE_TYPE_OPTIONS,
  type NewContentItem,
  type ContentCategory,
  type SpriteType,
} from '../data/newContentItems';

// ─── Persist state in localStorage ──────────────────────────────────────────
const STORAGE_KEY = 'tbp_content_editor_v1';

function loadSaved(): Record<string, Partial<NewContentItem>> {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
}

function saveToDisk(overrides: Record<string, Partial<NewContentItem>>) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(overrides));
}

// ─── Merge saved overrides with defaults ─────────────────────────────────────
function buildInitialItems(): NewContentItem[] {
  const saved = loadSaved();
  return NEW_CONTENT_ITEMS.map(item => ({
    ...item,
    ...(saved[item.id] ?? {}),
  }));
}

// ─── Category badge colors ────────────────────────────────────────────────────
const CAT_COLORS: Record<ContentCategory, string> = {
  weapon:            'bg-red-900/60 text-red-300 border-red-700',
  peanut_perc:       'bg-purple-900/60 text-purple-300 border-purple-700',
  buncha_bullshiiii: 'bg-amber-900/60 text-amber-300 border-amber-700',
  shrine:            'bg-cyan-900/60 text-cyan-300 border-cyan-700',
  enemy:             'bg-green-900/60 text-green-300 border-green-700',
};

const SPRITE_COLORS: Record<SpriteType, string> = {
  undecided:       'bg-zinc-800 text-zinc-400 border-zinc-600',
  sprite_only:     'bg-green-900/60 text-green-300 border-green-700',
  needs_animation: 'bg-amber-900/60 text-amber-300 border-amber-700',
};

// ─── Download helpers ─────────────────────────────────────────────────────────
function downloadJSON(items: NewContentItem[]) {
  const grouped: Record<string, NewContentItem[]> = {};
  for (const item of items) {
    if (!grouped[item.category]) grouped[item.category] = [];
    grouped[item.category].push(item);
  }
  const blob = new Blob([JSON.stringify(grouped, null, 2)], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = 'tbp_new_content.json';
  a.click();
  URL.revokeObjectURL(url);
}

function downloadManifest(items: NewContentItem[]) {
  const lines: string[] = [
    '# TheBurntPeanut Simulator 9000 — New Content Manifest',
    `# Generated: ${new Date().toISOString()}`,
    `# Total items: ${items.length}`,
    `# Approved: ${items.filter(i => i.approved).length}`,
    `# Needs animation: ${items.filter(i => i.spriteType === 'needs_animation').length}`,
    `# Sprite only: ${items.filter(i => i.spriteType === 'sprite_only').length}`,
    '',
  ];

  const categories = ['weapon', 'peanut_perc', 'buncha_bullshiiii', 'shrine', 'enemy'] as ContentCategory[];
  for (const cat of categories) {
    const catItems = items.filter(i => i.category === cat);
    if (!catItems.length) continue;
    const catLabel = CATEGORIES.find(c => c.id === cat)?.label ?? cat;
    lines.push(`\n${'='.repeat(60)}`);
    lines.push(`${catLabel.toUpperCase()} (${catItems.length} items)`);
    lines.push('='.repeat(60));

    const bySub: Record<string, NewContentItem[]> = {};
    for (const item of catItems) {
      const sub = item.subcategory ?? 'General';
      if (!bySub[sub]) bySub[sub] = [];
      bySub[sub].push(item);
    }

    for (const [sub, subItems] of Object.entries(bySub)) {
      lines.push(`\n  ── ${sub} ──`);
      for (const item of subItems) {
        const approved = item.approved ? '✅' : '⬜';
        const sprite = item.spriteType === 'sprite_only' ? '🖼️ ' : item.spriteType === 'needs_animation' ? '🎬 ' : '❓ ';
        lines.push(`  ${approved} ${sprite} ${item.proposedName}`);
        lines.push(`       ID: ${item.id}`);
        lines.push(`       Effect: ${item.effect}`);
        lines.push(`       Lore: ${item.loreReason}`);
        if (item.notes) lines.push(`       Notes: ${item.notes}`);
      }
    }
  }

  const blob = new Blob([lines.join('\n')], { type: 'text/plain' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = 'tbp_content_manifest.txt';
  a.click();
  URL.revokeObjectURL(url);
}

// ─── Main Component ───────────────────────────────────────────────────────────
export default function ContentEditor() {
  const [items, setItems] = useState<NewContentItem[]>(buildInitialItems);
  const [activeCategory, setActiveCategory] = useState<ContentCategory | 'all'>('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [showApprovedOnly, setShowApprovedOnly] = useState(false);
  const [expandedId, setExpandedId] = useState<string | null>(null);

  // Persist changes
  const updateItem = useCallback((id: string, patch: Partial<NewContentItem>) => {
    setItems(prev => {
      const next = prev.map(item => item.id === id ? { ...item, ...patch } : item);
      // Save overrides
      const saved = loadSaved();
      saved[id] = { ...saved[id], ...patch };
      saveToDisk(saved);
      return next;
    });
  }, []);

  const filteredItems = useMemo(() => {
    return items.filter(item => {
      if (activeCategory !== 'all' && item.category !== activeCategory) return false;
      if (showApprovedOnly && !item.approved) return false;
      if (searchQuery) {
        const q = searchQuery.toLowerCase();
        return (
          item.proposedName.toLowerCase().includes(q) ||
          item.originalDesignName.toLowerCase().includes(q) ||
          item.effect.toLowerCase().includes(q) ||
          item.loreReason.toLowerCase().includes(q) ||
          (item.subcategory ?? '').toLowerCase().includes(q)
        );
      }
      return true;
    });
  }, [items, activeCategory, searchQuery, showApprovedOnly]);

  const stats = useMemo(() => ({
    total: items.length,
    approved: items.filter(i => i.approved).length,
    needsAnimation: items.filter(i => i.spriteType === 'needs_animation').length,
    spriteOnly: items.filter(i => i.spriteType === 'sprite_only').length,
    undecided: items.filter(i => i.spriteType === 'undecided').length,
  }), [items]);

  const resetAll = () => {
    if (!confirm('Reset ALL changes to defaults?')) return;
    localStorage.removeItem(STORAGE_KEY);
    setItems(NEW_CONTENT_ITEMS.map(i => ({ ...i })));
  };

  return (
    <div className="min-h-screen bg-zinc-950 text-zinc-100" style={{ fontFamily: "'Share Tech Mono', monospace" }}>
      {/* ── Header ── */}
      <div className="border-b border-zinc-800 bg-zinc-900/80 sticky top-0 z-20 backdrop-blur">
        <div className="max-w-7xl mx-auto px-4 py-3 flex items-center justify-between gap-4 flex-wrap">
          <div>
            <h1 className="text-amber-400 text-xl font-bold tracking-widest uppercase">
              🥜 Content Editor
            </h1>
            <p className="text-zinc-500 text-xs mt-0.5">TheBurntPeanut Simulator 9000 — New Content Pipeline</p>
          </div>
          {/* Stats bar */}
          <div className="flex items-center gap-4 text-xs">
            <span className="text-zinc-400">{stats.total} items</span>
            <span className="text-green-400">✅ {stats.approved} approved</span>
            <span className="text-amber-400">🎬 {stats.needsAnimation} animated</span>
            <span className="text-green-300">🖼️ {stats.spriteOnly} sprite</span>
            <span className="text-zinc-500">❓ {stats.undecided} undecided</span>
          </div>
          {/* Actions */}
          <div className="flex gap-2">
            <button
              onClick={() => downloadManifest(items)}
              className="px-3 py-1.5 bg-amber-600 hover:bg-amber-500 text-black text-xs font-bold rounded transition-colors"
            >
              ↓ MANIFEST.TXT
            </button>
            <button
              onClick={() => downloadJSON(items)}
              className="px-3 py-1.5 bg-zinc-700 hover:bg-zinc-600 text-zinc-200 text-xs font-bold rounded transition-colors"
            >
              ↓ JSON
            </button>
            <button
              onClick={resetAll}
              className="px-3 py-1.5 bg-red-900/60 hover:bg-red-800 text-red-300 text-xs rounded transition-colors"
            >
              RESET
            </button>
          </div>
        </div>

        {/* Progress bar */}
        <div className="h-1 bg-zinc-800">
          <div
            className="h-full bg-amber-500 transition-all duration-500"
            style={{ width: `${(stats.approved / stats.total) * 100}%` }}
          />
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-6">
        {/* ── Filters ── */}
        <div className="flex flex-wrap gap-3 mb-6 items-center">
          {/* Category tabs */}
          <div className="flex gap-1 flex-wrap">
            <button
              onClick={() => setActiveCategory('all')}
              className={`px-3 py-1 text-xs rounded border transition-colors ${
                activeCategory === 'all'
                  ? 'bg-zinc-700 border-zinc-500 text-zinc-100'
                  : 'bg-zinc-900 border-zinc-700 text-zinc-400 hover:border-zinc-500'
              }`}
            >
              ALL ({items.length})
            </button>
            {CATEGORIES.map(cat => {
              const count = items.filter(i => i.category === cat.id).length;
              return (
                <button
                  key={cat.id}
                  onClick={() => setActiveCategory(cat.id)}
                  className={`px-3 py-1 text-xs rounded border transition-colors ${
                    activeCategory === cat.id
                      ? 'bg-zinc-700 border-zinc-500 text-zinc-100'
                      : 'bg-zinc-900 border-zinc-700 text-zinc-400 hover:border-zinc-500'
                  }`}
                >
                  {cat.label} ({count})
                </button>
              );
            })}
          </div>

          {/* Search */}
          <input
            type="text"
            placeholder="Search items..."
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
            className="flex-1 min-w-48 bg-zinc-900 border border-zinc-700 rounded px-3 py-1 text-xs text-zinc-200 placeholder-zinc-600 focus:outline-none focus:border-amber-600"
          />

          {/* Approved filter */}
          <label className="flex items-center gap-2 text-xs text-zinc-400 cursor-pointer select-none">
            <input
              type="checkbox"
              checked={showApprovedOnly}
              onChange={e => setShowApprovedOnly(e.target.checked)}
              className="accent-amber-500"
            />
            Approved only
          </label>

          <span className="text-zinc-600 text-xs">{filteredItems.length} shown</span>
        </div>

        {/* ── Item Grid ── */}
        <div className="grid gap-3">
          {filteredItems.map(item => (
            <ItemCard
              key={item.id}
              item={item}
              isExpanded={expandedId === item.id}
              onToggleExpand={() => setExpandedId(expandedId === item.id ? null : item.id)}
              onUpdate={patch => updateItem(item.id, patch)}
            />
          ))}
          {filteredItems.length === 0 && (
            <div className="text-center py-16 text-zinc-600 text-sm">
              No items match your filters.
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Item Card ────────────────────────────────────────────────────────────────
interface ItemCardProps {
  item: NewContentItem;
  isExpanded: boolean;
  onToggleExpand: () => void;
  onUpdate: (patch: Partial<NewContentItem>) => void;
}

function ItemCard({ item, isExpanded, onToggleExpand, onUpdate }: ItemCardProps) {
  const [editingName, setEditingName] = useState(false);
  const [nameInput, setNameInput] = useState(item.proposedName);
  const [notesInput, setNotesInput] = useState(item.notes);

  const commitName = () => {
    const trimmed = nameInput.trim();
    if (trimmed && trimmed !== item.proposedName) {
      onUpdate({ proposedName: trimmed });
    }
    setEditingName(false);
  };

  const commitNotes = () => {
    onUpdate({ notes: notesInput });
  };

  return (
    <div
      className={`border rounded-lg transition-all duration-200 ${
        item.approved
          ? 'border-green-800 bg-green-950/20'
          : 'border-zinc-800 bg-zinc-900/50'
      }`}
    >
      {/* ── Card Header (always visible) ── */}
      <div className="flex items-center gap-3 p-3 cursor-pointer" onClick={onToggleExpand}>
        {/* Approved checkbox */}
        <button
          onClick={e => { e.stopPropagation(); onUpdate({ approved: !item.approved }); }}
          className={`w-6 h-6 rounded border-2 flex items-center justify-center flex-shrink-0 transition-colors ${
            item.approved
              ? 'border-green-500 bg-green-500 text-black'
              : 'border-zinc-600 hover:border-green-500'
          }`}
        >
          {item.approved && <span className="text-xs font-bold">✓</span>}
        </button>

        {/* Category badge */}
        <span className={`text-xs px-2 py-0.5 rounded border flex-shrink-0 ${CAT_COLORS[item.category]}`}>
          {CATEGORIES.find(c => c.id === item.category)?.label.split(' ')[0]}
        </span>

        {/* Subcategory */}
        {item.subcategory && (
          <span className="text-zinc-600 text-xs flex-shrink-0 hidden sm:inline">
            [{item.subcategory}]
          </span>
        )}

        {/* Name — editable */}
        <div className="flex-1 min-w-0" onClick={e => e.stopPropagation()}>
          {editingName ? (
            <input
              autoFocus
              value={nameInput}
              onChange={e => setNameInput(e.target.value)}
              onBlur={commitName}
              onKeyDown={e => { if (e.key === 'Enter') commitName(); if (e.key === 'Escape') { setNameInput(item.proposedName); setEditingName(false); } }}
              className="w-full bg-zinc-800 border border-amber-600 rounded px-2 py-0.5 text-amber-300 text-sm focus:outline-none"
            />
          ) : (
            <span
              className="text-amber-300 text-sm font-bold hover:text-amber-200 cursor-text truncate block"
              title="Click to rename"
              onClick={() => { setEditingName(true); setNameInput(item.proposedName); }}
            >
              {item.proposedName}
              <span className="text-zinc-600 text-xs ml-2 font-normal">✏️</span>
            </span>
          )}
        </div>

        {/* Sprite type toggle */}
        <div className="flex gap-1 flex-shrink-0" onClick={e => e.stopPropagation()}>
          {SPRITE_TYPE_OPTIONS.map(opt => (
            <button
              key={opt.value}
              onClick={() => onUpdate({ spriteType: opt.value })}
              className={`px-2 py-0.5 text-xs rounded border transition-colors ${
                item.spriteType === opt.value
                  ? SPRITE_COLORS[opt.value]
                  : 'bg-zinc-900 border-zinc-700 text-zinc-600 hover:border-zinc-500'
              }`}
              title={opt.label}
            >
              {opt.label.split(' ')[0]}
            </button>
          ))}
        </div>

        {/* Notes indicator */}
        {item.notes && (
          <span className="text-zinc-500 text-xs flex-shrink-0" title={item.notes}>📝</span>
        )}

        {/* Expand chevron */}
        <span className="text-zinc-600 text-xs flex-shrink-0 transition-transform" style={{ transform: isExpanded ? 'rotate(180deg)' : '' }}>
          ▼
        </span>
      </div>

      {/* ── Expanded Details ── */}
      {isExpanded && (
        <div className="border-t border-zinc-800 px-4 pb-4 pt-3 grid gap-3">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-xs">
            {/* Original name */}
            <div>
              <div className="text-zinc-500 uppercase tracking-wider mb-1">Original Design Name</div>
              <div className="text-zinc-400">{item.originalDesignName}</div>
            </div>
            {/* ID */}
            <div>
              <div className="text-zinc-500 uppercase tracking-wider mb-1">ID</div>
              <div className="text-zinc-400 font-mono">{item.id}</div>
            </div>
            {/* Effect */}
            <div className="sm:col-span-2">
              <div className="text-zinc-500 uppercase tracking-wider mb-1">Effect</div>
              <div className="text-zinc-300">{item.effect}</div>
            </div>
            {/* Lore reason */}
            <div className="sm:col-span-2">
              <div className="text-zinc-500 uppercase tracking-wider mb-1">Lore Reason</div>
              <div className="text-amber-400/80 italic">"{item.loreReason}"</div>
            </div>
          </div>

          {/* Notes textarea */}
          <div>
            <div className="text-zinc-500 uppercase tracking-wider text-xs mb-1">Notes / Sprite Direction</div>
            <textarea
              value={notesInput}
              onChange={e => setNotesInput(e.target.value)}
              onBlur={commitNotes}
              placeholder="Add notes about sprite style, animation frames, pixel size, references..."
              rows={3}
              className="w-full bg-zinc-800 border border-zinc-700 rounded px-3 py-2 text-xs text-zinc-200 placeholder-zinc-600 focus:outline-none focus:border-amber-600 resize-none"
            />
          </div>

          {/* Sprite type full selector */}
          <div>
            <div className="text-zinc-500 uppercase tracking-wider text-xs mb-2">Sprite Classification</div>
            <div className="flex gap-2 flex-wrap">
              {SPRITE_TYPE_OPTIONS.map(opt => (
                <button
                  key={opt.value}
                  onClick={() => onUpdate({ spriteType: opt.value })}
                  className={`px-3 py-1.5 text-xs rounded border transition-colors ${
                    item.spriteType === opt.value
                      ? SPRITE_COLORS[opt.value]
                      : 'bg-zinc-900 border-zinc-700 text-zinc-500 hover:border-zinc-500'
                  }`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
            <div className="mt-2 text-zinc-600 text-xs">
              {item.spriteType === 'sprite_only' && '→ Single static sprite sheet. Pixel art, 32×32 or 64×64. No frame animation needed.'}
              {item.spriteType === 'needs_animation' && '→ Requires multiple animation frames. Specify idle/attack/death frames in notes.'}
              {item.spriteType === 'undecided' && '→ Not yet decided. Review effect and decide if movement/firing needs frames.'}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
